import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboards, createDashboard, addWidgetToDashboard } from '../../services/dashboardApi';
import { logger } from '../../utils/logger';
import './AddToDashboardModal.css';

/**
 * Modal for adding a query to a dashboard
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Callback to close modal
 * @param {number} props.queryId - ID of query to add
 * @param {string} props.currentChartType - Currently selected chart type
 * @returns {JSX.Element} Modal component
 */
const AddToDashboardModal = ({ isOpen, onClose, queryId, currentChartType }) => {
  const { t } = useTranslation();
  const { activeCompany } = useAuth();
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboardId, setSelectedDashboardId] = useState(null);
  const [widgetName, setWidgetName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDashboards, setIsLoadingDashboards] = useState(false);
  const [error, setError] = useState(null);

  // Fetch dashboards when modal opens
  useEffect(() => {
    if (isOpen && activeCompany?.id) {
      fetchDashboards();
    }
  }, [isOpen, activeCompany?.id]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWidgetName('');
      setError(null);
    }
  }, [isOpen]);

  const fetchDashboards = async () => {
    setIsLoadingDashboards(true);
    setError(null);
    try {
      const dashboardList = await getDashboards(activeCompany.id);
      
      if (dashboardList.length === 0) {
        // Auto-create default dashboard
        logger.info('No dashboards found, creating default dashboard');
        const defaultDashboard = await createDashboard(
          activeCompany.name,
          '2x2',
          4,
          activeCompany.id
        );
        setDashboards([defaultDashboard]);
        setSelectedDashboardId(defaultDashboard.id);
      } else {
        setDashboards(dashboardList);
        setSelectedDashboardId(dashboardList[0].id);
      }
    } catch (err) {
      logger.error('Failed to fetch dashboards:', err);
      setError(err.message || t('dashboards.error'));
    } finally {
      setIsLoadingDashboards(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!widgetName.trim()) {
      setError(t('errors.requiredField'));
      return;
    }

    if (!selectedDashboardId) {
      setError('No dashboard selected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await addWidgetToDashboard(
        selectedDashboardId,
        queryId,
        widgetName.trim(),
        currentChartType,
        activeCompany.id
      );
      
      logger.info('Widget added successfully');
      onClose(true); // Pass true to indicate success
    } catch (err) {
      logger.error('Failed to add widget:', err);
      setError(err.message || 'Failed to add widget to dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t('dashboards.addToDashboard')}</h2>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={isLoading}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {isLoadingDashboards ? (
              <div className="modal-loading">
                <div className="loading-spinner"></div>
                <p>{t('dashboards.loading')}</p>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="dashboard-select">
                    {t('dashboards.selectDashboard')}
                  </label>
                  <select
                    id="dashboard-select"
                    value={selectedDashboardId || ''}
                    onChange={(e) => setSelectedDashboardId(Number(e.target.value))}
                    disabled={isLoading || dashboards.length === 0}
                    required
                  >
                    {dashboards.map((dashboard) => (
                      <option key={dashboard.id} value={dashboard.id}>
                        {dashboard.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="widget-name">
                    {t('dashboards.widgetName')} *
                  </label>
                  <input
                    id="widget-name"
                    type="text"
                    value={widgetName}
                    onChange={(e) => setWidgetName(e.target.value)}
                    placeholder={t('dashboards.widgetNamePlaceholder')}
                    disabled={isLoading}
                    required
                    maxLength={100}
                  />
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || isLoadingDashboards || !widgetName.trim()}
            >
              {isLoading ? t('dashboards.saving') : t('dashboards.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddToDashboardModal;

